import React from 'react';
import {
    Box,
    Card,
    Chip,
    Skeleton,
    Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { trakrefFontFamily } from '../../../utils';
import { trakrefBlue, Grey2, Grey3, red } from '../../../color_utils';
import moment from 'moment';

const statusColors = {
    'Repaired': '#4caf50',
    'Not Repaired': red,
    'In Progress': '#ed6c02',
};

const RecentLeaks = ({ leaks = [], totalCount = 0, loading = false }) => {
    const fontBase = {
        fontFamily: trakrefFontFamily,
        fontSize: '.813rem',
    };

    if (loading) {
        return (
            <Box>
                <Skeleton variant="text" width={250} height={32} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={90} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 1 }} />
            </Box>
        );
    }

    if (!leaks || leaks.length === 0) {
        return (
            <Box sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTimeIcon sx={{ color: Grey3, fontSize: '1.25rem' }} />
                    <Typography sx={{ ...fontBase, fontSize: '1.1rem', color: Grey2, fontWeight: 600 }}>
                        Leak History
                    </Typography>
                </Box>
                <Typography sx={{ ...fontBase, color: Grey3, fontStyle: 'italic' }}>
                    There are no relevant records to display for this system
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccessTimeIcon sx={{ color: Grey3, fontSize: '1.25rem' }} />
                <Typography sx={{ ...fontBase, fontSize: '1.1rem', color: Grey2, fontWeight: 600 }}>
                    Recent Leaks
                </Typography>
                <Chip
                    label={`${totalCount} found`}
                    size="small"
                    sx={{ fontFamily: trakrefFontFamily, fontSize: '.75rem', backgroundColor: trakrefBlue, color: '#fff' }}
                />
            </Box>

            {leaks.map((leak, index) => (
                <Card variant="outlined" key={index} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 0.75, backgroundColor: '#f5f5f5' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography sx={{ ...fontBase, color: trakrefBlue, fontWeight: 600 }}>
                                WO-{leak.workOrderId}
                            </Typography>
                            <Typography sx={{ ...fontBase, color: Grey3 }}>
                                SE-{leak.serviceEventId}
                            </Typography>
                        </Box>
                        {leak.leakStatus && (
                            <Chip
                                label={leak.leakStatus}
                                size="small"
                                sx={{
                                    fontFamily: trakrefFontFamily,
                                    fontSize: '.75rem',
                                    fontWeight: 600,
                                    backgroundColor: statusColors[leak.leakStatus] || Grey3,
                                    color: '#fff',
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', px: 2, py: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ ...fontBase, color: Grey3, fontSize: '.7rem' }}>
                                Type of Service
                            </Typography>
                            <Typography sx={{ ...fontBase, color: Grey2 }}>
                                {leak.typeOfService}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ ...fontBase, color: Grey3, fontSize: '.7rem' }}>
                                Service Date
                            </Typography>
                            <Typography sx={{ ...fontBase, color: Grey2 }}>
                                {leak.serviceDate ? moment(leak.serviceDate).format('L') : '--'}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ ...fontBase, color: Grey3, fontSize: '.7rem' }}>
                                Leak Location
                            </Typography>
                            <Typography sx={{ ...fontBase, color: Grey2 }}>
                                {leak.leakLocationCategory && leak.leakLocationName
                                    ? `${leak.leakLocationCategory} \u2014 ${leak.leakLocationName}`
                                    : '--'}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ ...fontBase, color: Grey3, fontSize: '.7rem' }}>
                                Refrigerant Added
                            </Typography>
                            <Typography sx={{ ...fontBase, color: '#4caf50', fontWeight: 600 }}>
                                +{leak.refrigerantAdded?.toFixed(1) ?? '0.0'} lbs
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ ...fontBase, color: Grey3, fontSize: '.7rem' }}>
                                Refrigerant Recovered
                            </Typography>
                            <Typography sx={{ ...fontBase, color: red, fontWeight: 600 }}>
                                {'\u2212'}{leak.refrigerantRecovered?.toFixed(1) ?? '0.0'} lbs
                            </Typography>
                        </Box>
                    </Box>
                </Card>
            ))}
        </Box>
    );
};

export default RecentLeaks;
